import {
  getPlaybookNode,
  playbookNodeIdSchema,
  type FlattenedPlaybookNode,
  type PlaybookNodeId,
  type PlaybookTree,
} from "@repo/app-types/entities/playbook";
import {
  recordingAssessmentFeedbackStatusSchema,
  type RecordingAssessmentFeedbackStatus,
} from "@repo/app-types/entities/recordingAssessmentFeedback";
import { z } from "zod";
import type { AssessmentTreeItemKey } from "./recordingAssessmentEditor/assessmentTreeHelpers.js";

export const PLAYBOOK_SELECTION_QUERY_PARAM = "selectionId";
export const PLAYBOOK_FEEDBACK_STATUS_QUERY_PARAM = "status";
export const LEGACY_CRITERION_QUERY_PARAM = "criterion_id";

export const PlaybookSpecialSelectionId = {
  Outcome: "outcome",
  SummaryTemplate: "summary-template",
} as const;

export type PlaybookSpecialSelectionId =
  (typeof PlaybookSpecialSelectionId)[keyof typeof PlaybookSpecialSelectionId];

const playbookEditorSelectionIdSchema = z.union([
  z.enum([
    PlaybookSpecialSelectionId.Outcome,
    PlaybookSpecialSelectionId.SummaryTemplate,
  ]),
  playbookNodeIdSchema,
]);

export type PlaybookEditorSelectionId = z.infer<
  typeof playbookEditorSelectionIdSchema
>;

export interface EditPlaybookUrlState {
  /** The resolved initial editor selection. Outcome is the no-param default. */
  selectionKey: AssessmentTreeItemKey;
  /** Canonical query params, including legacy-param cleanup. */
  searchParams: URLSearchParams;
}

/**
 * Resolve the edit route's query state against the loaded draft tree. The
 * legacy criterion param is accepted only when the canonical param is absent.
 */
export function resolveEditPlaybookUrlState(
  currentSearchParams: URLSearchParams,
  tree: PlaybookTree,
): EditPlaybookUrlState {
  const searchParams = new URLSearchParams(currentSearchParams);
  const rawSelectionId = searchParams.get(PLAYBOOK_SELECTION_QUERY_PARAM);

  if (rawSelectionId === null) {
    const legacyCriterionId = playbookNodeIdSchema.safeParse(
      searchParams.get(LEGACY_CRITERION_QUERY_PARAM),
    );
    if (legacyCriterionId.success) {
      searchParams.set(PLAYBOOK_SELECTION_QUERY_PARAM, legacyCriterionId.data);
    }
  }
  searchParams.delete(LEGACY_CRITERION_QUERY_PARAM);

  const parsedSelectionId = playbookEditorSelectionIdSchema.safeParse(
    searchParams.get(PLAYBOOK_SELECTION_QUERY_PARAM),
  );
  const selectionKey = parsedSelectionId.success
    ? findAssessmentSelectionKey(tree, parsedSelectionId.data)
    : null;

  if (!selectionKey) {
    searchParams.delete(PLAYBOOK_SELECTION_QUERY_PARAM);
  }

  return {
    selectionKey:
      selectionKey ??
      ({
        type: "outcome",
        id: PlaybookSpecialSelectionId.Outcome,
      } as const),
    searchParams,
  };
}

export function parsePlaybookEditorSelectionId(
  rawSelectionId: string | null,
): PlaybookEditorSelectionId | null {
  return playbookEditorSelectionIdSchema.safeParse(rawSelectionId).data ?? null;
}

export function parseFeedbackStatus(
  rawStatus: string | null,
): RecordingAssessmentFeedbackStatus {
  return (
    recordingAssessmentFeedbackStatusSchema.safeParse(rawStatus).data ?? "open"
  );
}

/** Return the durable URL representation of an assessment-tree selection. */
export function getSelectionIdForAssessmentKey(
  key: AssessmentTreeItemKey | undefined,
): PlaybookEditorSelectionId | null {
  if (!key) {
    return null;
  }
  switch (key.type) {
    case "outcome":
      return PlaybookSpecialSelectionId.Outcome;
    case "summary-template":
      return PlaybookSpecialSelectionId.SummaryTemplate;
    case "section":
    case "criteria":
      return playbookNodeIdSchema.safeParse(key.id).data ?? null;
    default:
      // Ghost suggestions and add controls are session-only UI state.
      return null;
  }
}

export function checkIsLiveFeedbackSelection(
  selectionId: PlaybookEditorSelectionId | null,
  liveTree: PlaybookTree | undefined,
): selectionId is PlaybookNodeId {
  const parsedSelectionId = playbookNodeIdSchema.safeParse(selectionId);
  if (!liveTree || !parsedSelectionId.success) {
    return false;
  }
  return getPlaybookNode(parsedSelectionId.data, liveTree) !== undefined;
}

export interface FeedbackReviewUrlState {
  selectedNodeId: PlaybookNodeId | null;
  status: RecordingAssessmentFeedbackStatus;
  searchParams: URLSearchParams;
}

/** Build query state after a manual node or status change. */
export function buildManualFeedbackReviewSearchParams(props: {
  currentSearchParams: URLSearchParams;
  selectedNodeId: PlaybookNodeId | null;
  status: RecordingAssessmentFeedbackStatus;
}): URLSearchParams {
  const { currentSearchParams, selectedNodeId, status } = props;
  const searchParams = new URLSearchParams(currentSearchParams);
  if (selectedNodeId) {
    searchParams.set(PLAYBOOK_SELECTION_QUERY_PARAM, selectedNodeId);
  } else {
    searchParams.delete(PLAYBOOK_SELECTION_QUERY_PARAM);
  }
  searchParams.set(PLAYBOOK_FEEDBACK_STATUS_QUERY_PARAM, status);
  return searchParams;
}

export function resolveFeedbackReviewUrlState(props: {
  currentSearchParams: URLSearchParams;
  flattenedNodes: readonly FlattenedPlaybookNode[];
  fallbackNodeId: PlaybookNodeId | null;
}): FeedbackReviewUrlState {
  const { currentSearchParams, flattenedNodes, fallbackNodeId } = props;
  const searchParams = new URLSearchParams(currentSearchParams);
  const validNodeIds = new Set(flattenedNodes.map((entry) => entry.node.id));

  const parsedSelectionId = playbookNodeIdSchema.safeParse(
    searchParams.get(PLAYBOOK_SELECTION_QUERY_PARAM),
  ).data;
  const selectedNodeId =
    parsedSelectionId && validNodeIds.has(parsedSelectionId)
      ? parsedSelectionId
      : fallbackNodeId;
  const status = parseFeedbackStatus(
    searchParams.get(PLAYBOOK_FEEDBACK_STATUS_QUERY_PARAM),
  );

  if (selectedNodeId) {
    searchParams.set(PLAYBOOK_SELECTION_QUERY_PARAM, selectedNodeId);
  } else {
    searchParams.delete(PLAYBOOK_SELECTION_QUERY_PARAM);
  }
  searchParams.set(PLAYBOOK_FEEDBACK_STATUS_QUERY_PARAM, status);

  return {
    selectedNodeId,
    status,
    searchParams,
  };
}

function findAssessmentSelectionKey(
  tree: PlaybookTree,
  selectionId: PlaybookEditorSelectionId,
): AssessmentTreeItemKey | null {
  if (selectionId === PlaybookSpecialSelectionId.Outcome) {
    return { type: "outcome", id: PlaybookSpecialSelectionId.Outcome };
  }
  if (selectionId === PlaybookSpecialSelectionId.SummaryTemplate) {
    return {
      type: "summary-template",
      id: PlaybookSpecialSelectionId.SummaryTemplate,
    };
  }

  const node = getPlaybookNode(selectionId, tree);
  if (!node) {
    return null;
  }
  return node.type === "section"
    ? { type: "section", id: node.id }
    : { type: "criteria", id: node.id };
}
